import { ETAPA_VENTA_CERRADA } from "../pipelineUtils";
import {
  ETAPAS_MADURAS_SIN_MONTO,
  oportunidadEstaAbierta,
} from "./expansionConstants";

const hoyISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function ventasValidasPorLead(ventas, leadId) {
  return (ventas || []).filter((v) => v.leadId === leadId && !v.anulada);
}

export function leadEsCliente(ventas, leadId) {
  return ventasValidasPorLead(ventas, leadId).length > 0;
}

export function revenueHistoricoCliente(ventas, leadId) {
  return Math.round(
    ventasValidasPorLead(ventas, leadId).reduce((s, v) => s + (Number(v.total) || 0), 0) * 100
  ) / 100;
}

export function metricasCliente(ventas, leadId) {
  const vs = ventasValidasPorLead(ventas, leadId).sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
  const revenue = revenueHistoricoCliente(ventas, leadId);
  const compras = vs.length;
  const ticketPromedio = compras > 0 ? Math.round((revenue / compras) * 100) / 100 : 0;
  const primeraCompra = vs[0]?.fecha?.slice(0, 10) || null;
  const ultimaCompra = vs[compras - 1]?.fecha?.slice(0, 10) || null;
  return { revenue, compras, ticketPromedio, primeraCompra, ultimaCompra };
}

export function pipelineNuevos(leads, oportunidades = []) {
  const explicit = new Set(oportunidades.map(o => o.leadId));
  return Math.round(
    (leads || [])
      .filter((l) => !explicit.has(l.id) && l.etapa !== "perdido" && l.etapa !== ETAPA_VENTA_CERRADA)
      .reduce((s, l) => {
        const n = Number(l.montoEstimado);
        return s + (Number.isFinite(n) && n > 0 ? n : 0);
      }, 0) * 100
  ) / 100;
}

export function oportunidadesAbiertas(oportunidades) {
  return (oportunidades || []).filter(oportunidadEstaAbierta);
}

export function pipelineExpansion(oportunidades) {
  return Math.round(
    oportunidadesAbiertas(oportunidades).reduce((s, o) => {
      const n = Number(o.montoEstimado);
      return s + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0) * 100
  ) / 100;
}

export function pipelineExpansionCliente(oportunidades, leadId) {
  return pipelineExpansion((oportunidades || []).filter((o) => o.leadId === leadId));
}

export function pipelineTotal(leads, oportunidades) {
  return Math.round((pipelineNuevos(leads, oportunidades) + pipelineExpansion(oportunidades)) * 100) / 100;
}

export function valorPotencialCliente(ventas, oportunidades, leadId) {
  return Math.round(
    (revenueHistoricoCliente(ventas, leadId) + pipelineExpansionCliente(oportunidades, leadId)) * 100
  ) / 100;
}

export function revenueCerradoGlobal(ventasActivas) {
  return Math.round(
    (ventasActivas || []).reduce((s, v) => s + (Number(v.total) || 0), 0) * 100
  ) / 100;
}

export function revenueHistoricoClientesExistentes(leads, ventas, oportunidades) {
  const ids = new Set(
    (oportunidades || []).map((o) => o.leadId).concat(
      (ventas || []).filter((v) => !v.anulada && v.leadId).map((v) => v.leadId)
    )
  );
  let total = 0;
  ids.forEach((id) => {
    if (leadEsCliente(ventas, id)) total += revenueHistoricoCliente(ventas, id);
  });
  return Math.round(total * 100) / 100;
}

function diasDesde(iso) {
  if (!iso) return Infinity;
  const t = new Date(iso.slice(0, 10)).getTime();
  if (!Number.isFinite(t)) return Infinity;
  return Math.floor((Date.now() - t) / 86400000);
}

export function senalesOportunidad(opp, { diasEstancamiento = 7 } = {}) {
  const hoy = hoyISO();
  const senales = [];
  if (oportunidadEstaAbierta(opp)) {
    if (opp.proximoSeguimiento && opp.proximoSeguimiento < hoy) senales.push("seguimiento_vencido");
    if (opp.fechaCierreEsperada && opp.fechaCierreEsperada < hoy) senales.push("cierre_vencido");
    if (!opp.vendedorId) senales.push("sin_responsable");
    if (!opp.proximaAccion?.trim()) senales.push("sin_proxima_accion");
    const ultimo = opp.ultimoContacto || opp.actualizadoEn || opp.creadoEn;
    if (diasDesde(ultimo) >= diasEstancamiento) senales.push("sin_actividad");
    if (ETAPAS_MADURAS_SIN_MONTO.includes(opp.etapa) && !(Number(opp.montoEstimado) > 0)) {
      senales.push("madura_sin_monto");
    }
    const ultimaEtapa = opp.historialEtapas?.length
      ? opp.historialEtapas[opp.historialEtapas.length - 1]?.fechaHora
      : opp.creadoEn;
    if (diasDesde(ultimaEtapa) >= diasEstancamiento) senales.push("estancada");
  }
  if (opp.etapa === "pospuesta" && !opp.proximoSeguimiento) senales.push("pospuesta_sin_fecha");
  return senales;
}

export function oportunidadAltoValor(opp, oportunidades) {
  const montos = oportunidadesAbiertas(oportunidades)
    .map((o) => Number(o.montoEstimado))
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  const m = Number(opp.montoEstimado);
  if (!m || montos.length < 3) return false;
  const mediana = montos[Math.floor(montos.length / 2)];
  return m >= mediana * 1.25;
}

export function resumenComercialGlobal({ leads, ventas, oportunidades, ventasActivas, diasEstancamiento = 7 }) {
  const abiertas = oportunidadesAbiertas(oportunidades);
  const sinEstimacion = abiertas.filter((o) => !(Number(o.montoEstimado) > 0)).length;
  const vencidas = abiertas.filter((o) => senalesOportunidad(o, { diasEstancamiento }).includes("seguimiento_vencido")).length;
  const porLead = {};
  abiertas.forEach((o) => { porLead[o.leadId] = (porLead[o.leadId] || 0) + 1; });
  const clientesMultiples = Object.values(porLead).filter((c) => c > 1).length;
  return {
    pipelineNuevos: pipelineNuevos(leads, oportunidades),
    pipelineExpansion: pipelineExpansion(oportunidades),
    pipelineTotal: pipelineTotal(leads, oportunidades),
    revenueCerrado: revenueCerradoGlobal(ventasActivas),
    revenueHistoricoClientes: revenueHistoricoClientesExistentes(leads, ventas, oportunidades),
    oportunidadesAbiertas: abiertas.length,
    sinEstimacion,
    seguimientoVencido: vencidas,
    clientesMultiplesOportunidades: clientesMultiples,
  };
}
